import cn from 'classnames';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import PlayPauseIcon from './parts/PlayPauseIcon.tsx';
import styles from './VideoPlayer.module.scss';
import useVideoTracking from '@/widgets/hooks/useVideoTracking.tsx';

type HTMLVideoControls =
  | 'nodownload'
  | 'noremoteplayback'
  | 'noplaybackrate'
  | 'nofullscreen';
const DEFAULT_VIDEO_CONTROLS: HTMLVideoControls[] = [
  'nodownload',
  'noremoteplayback',
  'noplaybackrate'
];

export type VideoPlayerProps = Omit<
  JSX.HTMLAttributes<HTMLVideoElement>,
  'autoplay' | 'muted' | 'controls'
> & {
  source: { url: string; type: string }[];
  translations: { noVideoSupportLabel: string; playPauseButtonLabel: string };
  hasReducedMotion: boolean;
  class?: string;
  controls?: HTMLVideoControls[];
  track?: JSX.HTMLAttributes<HTMLTrackElement>;
  autoPlay?: boolean;
  loop?: boolean;
  poster?: string;
  /**
   * External prop to control whether or not the video can be played.
   * Also, if this prop changes while the video is playing then the video will pause
   */
  canPlay?: boolean;
};

const defaultTrack = {
  src: '',
  label: '',
  kind: 'captions',
  default: true
} as JSX.HTMLAttributes<HTMLTrackElement>;

export default function VideoPlayer({
  source,
  translations,
  class: className,
  controls = DEFAULT_VIDEO_CONTROLS,
  hasReducedMotion,
  track = defaultTrack,
  canPlay = true,
  ...props
}: VideoPlayerProps) {
  const { autoPlay = true, loop = true, ref, ...restProps } = props;
  const [isPlaying, setIsPlaying] = useState(false);
  const controlsList = controls.join(' ');

  const { lastEventSource, trackVideoPlay, trackVideoPause, videoRef } =
    useVideoTracking(source[0].url, ref);

  const play = (): void => {
    if (!canPlay) {
      return;
    }
    videoRef?.current?.play();
    setIsPlaying(true);
  };

  const pause = (): void => {
    videoRef?.current?.pause();
    setIsPlaying(false);
  };

  const handlePlayPauseButton = () => {
    lastEventSource.value = 'default image';
    if (isPlaying) {
      trackVideoPause();
      pause();
    } else {
      trackVideoPlay();
      play();
    }
  };

  useEffect(() => {
    if (isPlaying && !canPlay) {
      pause();
    }
  }, [canPlay, isPlaying]);

  // autoplay
  useEffect(() => {
    if (canPlay && autoPlay && !isPlaying && !hasReducedMotion) {
      play();
    }
  }, [canPlay, autoPlay]);

  // loop the video at the end
  useEffect(() => {
    if (loop) {
      videoRef.current?.addEventListener('ended', () => {
        videoRef?.current?.play();
      });
    }
  }, [loop, videoRef.current]);

  return (
    <div class={cn(styles.container, className)}>
      {/*
        NOTE: we need to manage autoplay ourselves because the looping and events
              are very specific, we need fine grained control.
        eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        class={styles.video}
        ref={videoRef}
        muted
        autoPlay={false}
        loop={false}
        controlsList={controlsList}
        onPlay={play}
        onPause={pause}
        playsInline
        disablePictureInPicture
        {...restProps}
      >
        {source.map((s) => (
          <source key={s.url} src={s.url} type={s.type} />
        ))}
        <track {...track} />
        {translations.noVideoSupportLabel}
      </video>

      <button
        type="button"
        class={cn(styles.playPauseButton, { [styles.animate]: isPlaying })}
        aria-pressed={isPlaying}
        aria-label={translations.playPauseButtonLabel}
        onClick={handlePlayPauseButton}
      >
        <span class={styles.buttonCircle}>
          <PlayPauseIcon isPlaying={isPlaying} />
        </span>
      </button>
    </div>
  );
}
